import { loadSettings } from "./settings-loader.js";
import { initPlayer } from "./player-init.js";
import { burstIframes, collapseIframes, safeDestroy } from "./player-lifecycle.js"

import { filterPlayablePrerolls } from "../ads/ads-manager.js";
import { getUniversalConsent } from "../ads/cookie-consent.js";

import { injectCleonStylesAndWait } from "./assets-loader.js";
import { buildPlayerDom, moveContainerToParagraph, hidePending, showPending } from "./container-dom.js";
import { addConsentToZoneUrl, replaceKnownAdMacros } from "../ads/consent-bridge.js";

import { switchToOMP } from "./omp-mode.js";

import { getDebugMode, createLogger, PLAYER_LOG_EVENTS, logPlayerEvent } from "../helpers/logger.js";

function injectCleonCriticalStyles() {
  if (document.getElementById("cleon-critical-styles")) return;

  const style = document.createElement("style");
  style.id = "cleon-critical-styles";
  style.textContent = `
    .cleon-container.ad-slot--pending,
    .ad-slot--pending {
      height: 0 !important;
      min-height: 0 !important;
      max-height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      opacity: 0 !important;
      visibility: hidden !important;
      pointer-events: none !important;
      background: transparent !important;
    }
  `;

  document.head.appendChild(style);
}

export async function initCleonContainer(container) {
  const debug = getDebugMode(container);
  const log = createLogger("ContainerInit", debug);
  injectCleonCriticalStyles();
  hidePending(container);
  console.log("🧪 CONTAINER INIT BUILD MARKER 2026-06-16-A");
  try {
    if (container.__cleonInitializing || container.__cleonInitialized) return;
    container.__cleonInitializing = true;

    const zoneId =
      container.getAttribute("data-cleon-zone") ||
      container.getAttribute("data") ||
      null;

    if (!zoneId) {
      container.__cleonInitializing = false;
      log.warn("Missing zoneId", container);
      return;
    }

    if (!container.id)
      container.id = `cleon-container-${Math.random().toString(36).slice(2)}`;

    const playerId = `cleon-player-${zoneId}`;

    log.debug("Initializing Cleon Player", {
      zoneId,
      playerId,
      debug,
    });

    logPlayerEvent(log, PLAYER_LOG_EVENTS.PLAYER_LOADED, {
      zoneId,
      playerId,
      containerId: container.id,
    });

    const rawCustomVideoId = container.getAttribute("data-video");
    const customVideoId =
      rawCustomVideoId !== null && String(rawCustomVideoId).trim() !== ""
        ? String(rawCustomVideoId).trim()
        : null;

    const consent = await getUniversalConsent(8000, debug);

    log.debug("🧪 consent resolved before ad URL build", {
      source: consent?.source,
      consentStatus: consent?.consentStatus,
      consentElapsedMs: consent?.consentElapsedMs,
      gdprApplies: consent?.gdprApplies,
      hasTcString: !!consent?.tcString,
      tcStringLength: consent?.tcString?.length || 0,
      hasGppString: !!consent?.gppString,
      gppSid: consent?.gppSid,
    });
    if (!consent.allowed) {
      log.debug("No consent, skipping player init", { zoneId });

      container.__cleonInitializing = false;
      container.__cleonWaitingConsent = true;
      container.hidden = false;
      return;
    }

    const settings = await loadSettings(zoneId, customVideoId, debug);

    if (customVideoId !== null) {
      container.classList.remove("ad-slot--pending");
    }

    logPlayerEvent(log, PLAYER_LOG_EVENTS.CONFIG_LOADED, {
      zoneId,
      mode: settings?.mode || null,
      videoCount: Array.isArray(settings?.videos) ? settings.videos.length : 0,
      hasAds: !!settings?.ads,
    });

    const hasVideos =
      (Array.isArray(settings?.videos) && settings.videos.length > 0) ||
      !!settings?.video?.src ||
      (Array.isArray(settings?.playlist) && settings.playlist.length > 0);
    container.__cleonPassbackSnippet =
        typeof settings?.ads?.passback === "string"
          ? settings.ads.passback
          : "";
    if (!hasVideos) {
      log.debug("⛔ No videos in JSON config → skip player init and iframe burst");

      hidePending(container);
      collapseIframes();

      container.hidden = true;
      container.__cleonInitialized = false;
      container.__cleonInitializing = false;
      container.__cleonWaitingConsent = false;
      return;
    }
    
    const cssReady = injectCleonStylesAndWait();
    await cssReady;

    const paragraphIndex = Number(settings.paragraph ?? 0);
    await moveContainerToParagraph(container, paragraphIndex);

    if (!container.querySelector("video")) {
      buildPlayerDom(container, playerId);
    }

    const wrapperSelector = `#${container.id} .video-wrapper-fluid, #${container.id} .video-wrapper-fixed`;
    const playerContainerSelector = `#${container.id} .player-container`;

    const mode = settings.mode;
    function normalizeAdUrls(value) {
      if (Array.isArray(value)) return value.filter(Boolean);
      if (typeof value === "string" && value.trim()) return [value.trim()];
      return [];
    }

    function normalizeAdUrlsForVastCheck(value) {
      return normalizeAdUrls(value).map((url) => replaceKnownAdMacros(url));
    }

    if (consent?.gdprApplies !== false && !consent?.tcString) {
      log.debug("⚠ Building ad URLs without gdpr_consent", {
        source: consent?.source,
        consentStatus: consent?.consentStatus,
        gdprApplies: consent?.gdprApplies,
        hasTcString: !!consent?.tcString,
        hasGppString: !!consent?.gppString,
        gppSid: consent?.gppSid,
      });
    }
    
    const [prerollUrls, fallbackUrls, midrollUrls, postrollUrls] = await Promise.all([
      filterPlayablePrerolls(
        normalizeAdUrlsForVastCheck(settings?.ads?.preroll),
        consent,
        debug
      ),
      filterPlayablePrerolls(
        normalizeAdUrlsForVastCheck(settings?.ads?.fallback),
        consent,
        debug
      ),
      filterPlayablePrerolls(
        normalizeAdUrlsForVastCheck(settings?.ads?.midroll),
        consent,
        debug
      ),
      filterPlayablePrerolls(
        normalizeAdUrlsForVastCheck(settings?.ads?.postroll),
        consent,
        debug
      ),
    ]);
    
    const state = {
      prerollUrls: prerollUrls.map(u => addConsentToZoneUrl(u, consent)),
      fallbackUrls: fallbackUrls.map(u => addConsentToZoneUrl(u, consent)),
      midrollUrls: midrollUrls.map(u => addConsentToZoneUrl(u, consent)),
      postrollUrls: postrollUrls.map(u => addConsentToZoneUrl(u, consent)),
      prerollIndex: 0,
      fallbackIndex: 0,
      midrollIndex: 0,
      postrollIndex: 0,
      currentType: null,
      consent: consent,
      mode: settings.mode,
      hasContentVideo: !!settings?.video?.src,
      contentVideoSrc: settings?.video?.src || null,
      hasExplicitContentVideo: !!customVideoId,
      adsOptional: settings.mode === "monetized",
    };

    if (state.prerollUrls.length === 0 && state.fallbackUrls.length === 0) {
      const canContinueToContent = !!settings?.video?.src;

      if (!canContinueToContent) {
        const hasPassback = !!container.__cleonPassbackSnippet?.trim?.();

        if (hasPassback) {
          log.debug("🎯 No playable ads and no data-video → load passback");
          safeDestroy(null, null, settings, container, "yes", debug);
        } else {
          log.debug("⛔ No ads, no data-video, and no passback → do not continue content");
          hidePending(container);
          container.hidden = true;
        }

        container.__cleonInitialized = false;
        container.__cleonInitializing = false;
        container.__cleonWaitingConsent = false;
        return;
      }

      if (settings.mode !== "monetized") {
        const hasPassback = !!container.__cleonPassbackSnippet?.trim?.();

        if (hasPassback) {
          log.debug("🎯 No playable ads in non-monetized mode → load passback");
          safeDestroy(null, null, settings, container, "yes", debug);
        } else {
          log.debug("⛔ No playable ads in non-monetized mode and no passback → stay collapsed");
          hidePending(container);
          collapseIframes();
          container.hidden = true;
        }

        container.__cleonInitialized = false;
        container.__cleonInitializing = false;
        container.__cleonWaitingConsent = false;
        return;
      }

      log.debug("No playable ads available in monetized mode → continue content");

      container.hidden = false;
      container.removeAttribute("hidden");

      const imaOptions =
        (mode === "standard" || mode === "pmp") && state.prerollUrls?.length > 0
          ? {
              id: playerId,
              adTagUrl: state.prerollUrls[0],
              requestMode: "onPlay"
            }
          : null;

      const { player, dom } = await initPlayer(settings, {
        videoElementId: playerId,
        playerContainerSelector,
        wrapperSelector,
        imaOptions,
        debug,
      });

      if (settings?.video?.src) {
        player.src({
          src: settings.video.src,
          type:
            settings.video.type ||
            (settings.video.src.endsWith(".m3u8")
              ? "application/x-mpegURL"
              : "video/mp4"),
        });

        if (settings.video.poster) {
          player.poster(settings.video.poster);
        }
        // showPending(container);
      } else {
        log.debug("⚠ No content video source configured");
        hidePending(container);
        safeDestroy(player, dom, settings, container, "yes", debug);
        return;
      }

      container.__cleonInitialized = true;
      container.__cleonInitializing = false;
      container.__cleonWaitingConsent = false;

      

      log.debug("continue-content source:", player.currentSources?.(), player.currentSrc?.());

      return;
    }

    const imaOptions =
      (mode === "standard" || mode === "pmp") && state.prerollUrls?.length > 0
        ? {
            id: playerId,
            adTagUrl: state.prerollUrls[0],
            requestMode: "onPlay"
          }
        : null;

    const { player, dom } = await initPlayer(settings, {
      videoElementId: playerId,
      playerContainerSelector,
      wrapperSelector,
      imaOptions,
      debug,
    });
    
    if (!player) {
      log.debug("⚠ No player created (passback only)");
      container.__cleonInitializing = false;
      return;
    }

    if (settings?.video?.src) {
      player.src({
        src: settings.video.src,
        type: settings.video.type || (settings.video.src?.endsWith(".m3u8")
          ? "application/x-mpegURL"
          : "video/mp4")
      });

      if (settings.video.poster) {
        player.poster(settings.video.poster);
      }
    }

    log.debug("player sources:", player.currentSources?.(), "currentSrc:", player.currentSrc?.());


    container.__cleonInitialized = true;
    container.__cleonInitializing = false;
    container.__cleonWaitingConsent = false;

    if (mode === "omp" || mode === "monetized") {
      if (state.adsOptional) {
        container.hidden = false;
      } else {
        container.hidden = true;
      }

      hidePending(container);
      await switchToOMP(container, settings, state, playerId);
    }

    if (mode === "standard" || mode === "pmp") {

      if (state.prerollUrls?.length > 0) {
        container.hidden = true;
        hidePending(container);

        if (
          !player.ima ||
          typeof player.ima.requestAds !== "function" ||
          typeof player.ima.initializeAdDisplayContainer !== "function"
        ) {
          log.debug("🚫 IMA plugin missing/incomplete", {
            ima: !!player.ima,
            requestAds: typeof player.ima?.requestAds,
            init: typeof player.ima?.initializeAdDisplayContainer,
            changeAdTag: typeof player.ima?.changeAdTag,
          });

          safeDestroy(player, dom, settings, container, "yes", debug);
          return;
        }

        let pmpFinished = false;
        let pmpSwitchedToOmp = false;
        let pmpHadStarted = false;
        let pmpLoadTimer = null;

        const clearPmpLoadTimer = () => {
          if (pmpLoadTimer) {
            clearTimeout(pmpLoadTimer);
            pmpLoadTimer = null;
          }
        };

        const startPmpLoadTimer = () => {
          clearPmpLoadTimer();

          pmpLoadTimer = setTimeout(() => {
            if (
              pmpFinished ||
              pmpSwitchedToOmp ||
              pmpHadStarted ||
              player.__adPlaying
            ) {
              return;
            }

            switchPmpToOmp("⏱ PMP ad load timeout → switch to OMP", {
              message: "PMP ad load timeout",
            });
          }, 8000);
        };

        const getAdErrorCode = (e) => {
          try {
            return (
              e?.data?.AdError?.getErrorCode?.() ??
              e?.data?.adError?.getErrorCode?.() ??
              e?.data?.adsRequestError?.getErrorCode?.() ??
              e?.error?.getErrorCode?.() ??
              e?.adsError?.getErrorCode?.() ??
              null
            );
          } catch {
            return null;
          }
        };

        const getAdErrorMessage = (e) => {
          try {
            return (
              e?.data?.AdError?.toString?.() ??
              e?.data?.adError?.toString?.() ??
              e?.error?.toString?.() ??
              String(e?.message || "")
            );
          } catch {
            return "";
          }
        };

        const isNoFillLikeError = (e) => {
          const code = getAdErrorCode(e);
          const msg = getAdErrorMessage(e);

          return (
            code === 303 ||
            /No Ads VAST response after one or more Wrappers/i.test(msg)
          );
        };

        const switchPmpToOmp = (label, e) => {
          const code = getAdErrorCode(e);
          const msg = getAdErrorMessage(e);

          if (pmpFinished || pmpSwitchedToOmp) return;

          pmpSwitchedToOmp = true;
          pmpFinished = true;
          clearPmpLoadTimer();
          cancelReveal();

          log.debug(label, {
            code,
            msg,
            noFillLike: isNoFillLikeError(e),
            preroll: state.prerollUrls,
            fallback: state.fallbackUrls,
          });

          setTimeout(() => {
            log.debug(">>> SWITCHING TO OMP NOW", {
              preroll: state.prerollUrls,
              fallback: state.fallbackUrls,
            });

            void switchToOMP(container, settings, state, playerId);
          }, 0);
        };
        const v = player.el()?.querySelector("video");
        if (v) {
          v.muted = true;
          v.setAttribute("muted", "");
          v.volume = 0;
          v.playsInline = true;
          v.setAttribute("playsinline", "");
        }

        player.muted(true);
        player.__adPlaying = false;

        let revealed = false;
        let canceled = false;
        let revealTimer = null;

        const cancelReveal = () => {
          canceled = true;
          revealed = true;

          if (revealTimer) {
            clearTimeout(revealTimer);
            revealTimer = null;
          }
          collapseIframes();
          hidePending(container);
          player.off("adstart", onStart);
          player.off("ads-ad-started", onStart);
        };

        const onStart = () => {
          if (canceled || pmpFinished || revealed) return;
          pmpHadStarted = true;
          player.__adPlaying = true;
          clearPmpLoadTimer();
          if (revealTimer) clearTimeout(revealTimer);

          revealTimer = setTimeout(() => {
            if (canceled || pmpFinished || revealed) return;
            if (!player.__adPlaying) return;

            revealed = true;

            container.hidden = false;
            container.removeAttribute("hidden");

            burstIframes();
            showPending(container);

            log.debug("🔥 PMP preroll started → reveal");
          }, 250);
        };

        const finish = (label, e) => {
          if (pmpFinished) return;

          pmpFinished = true;
          clearPmpLoadTimer();
          cancelReveal();

          if (e) log.debug(label, e);
          else log.debug(label);

          safeDestroy(player, dom, settings, container, "no", debug);
        };

        const handlePmpError = (label, e) => {
          const code = getAdErrorCode(e);
          const msg = getAdErrorMessage(e);

          log.debug("PMP error caught", { code, msg, event: e });

          player.__adPlaying = false;

          switchPmpToOmp(label, e);
        };

        const handlePmpEnded = (label, e) => {
          player.__adPlaying = false;

          if (!pmpHadStarted) {
            switchPmpToOmp("⚠ PMP ended before start → switch to OMP", e);
            return;
          }

          finish(label, e);
        };

        let imaPrimed = false;

        const primeIMA = async (source) => {
          if (pmpFinished) return;

          try {
            player.ima.initializeAdDisplayContainer();
          } catch (e) {
            log.debug("IMA init fail", e);
          }

          log.debug("IMA methods", {
            init: !!player.ima?.initializeAdDisplayContainer,
            req: !!player.ima?.requestAds
          });

          try {
            startPmpLoadTimer();
            player.ima.requestAds();
          } catch (e) {
            clearPmpLoadTimer();
            log.debug("IMA requestAds fail", source, e);
            imaPrimed = false;
            switchPmpToOmp("❌ PMP requestAds failed → switch to OMP", e);
            return;
          }

          try {
            await player.play();
          } catch (e) {
            log.debug("play blocked", e);
            imaPrimed = false;
          }
        };

        const onGesture = (e) => {
          if (e?.button === 2) return;
          if (imaPrimed) return;
          imaPrimed = true;
          void primeIMA("gesture");
        };

        player.ready(() => {
          if (window.PointerEvent) {
            container.addEventListener("pointerdown", onGesture, { once: true, passive: true });
          } else {
            container.addEventListener("touchstart", onGesture, { once: true, passive: true });
            container.addEventListener("mousedown", onGesture, { once: true, passive: true });
          }
        });

        player.on("adstart", onStart);
        player.on("ads-ad-started", onStart);

        player.one("adend", (e) => handlePmpEnded("✅ PMP finished → destroy & hide", e));
        player.one("ads-ad-ended", (e) => handlePmpEnded("✅ PMP finished → destroy & hide", e));

        player.on("adserror", (e) => handlePmpError("❌ PMP error → switch to OMP", e));
        player.on("ads-ad-error", (e) => handlePmpError("❌ PMP error → switch to OMP", e));

        player.on("adsready", () => log.debug("ADS READY"));
        player.on("adstart", () => log.debug("AD START"));
        player.on("adserror", (e) => log.debug("AD ERROR", e));

        return;
      }

      log.debug("ℹ No PMP ads → direct OMP");

      await switchToOMP(container, settings, state, playerId);
      return;
    }

  } catch (err) {
    container.__cleonInitializing = false;
    container.__cleonInitialized = false;
    log.debug("❌ Cleon init error:", err);
  }
}